import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Modal } from '@/shared/components/ui/Modal';
import { Input } from '@/shared/components/ui/Input';
import { Button } from '@/shared/components/ui/Button';
import { useToast } from '@/shared/components/ui/Toast';
import { useCreateUser, useLocations } from '../hooks/useAdmin';

const userInviteSchema = z.object({
  email: z.string().email('Invalid email address'),
  name: z.string().min(1, 'Name is required'),
  location_ids: z.array(z.string()).min(1, 'At least one location is required'),
  is_admin: z.boolean(),
});

type UserInviteFormData = z.infer<typeof userInviteSchema>;

interface UserInviteFormProps {
  isOpen: boolean;
  onClose: () => void;
}

export function UserInviteForm({ isOpen, onClose }: UserInviteFormProps) {
  const { showToast } = useToast();
  const { data: locations } = useLocations();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<UserInviteFormData>({
    resolver: zodResolver(userInviteSchema),
    defaultValues: {
      email: '',
      name: '',
      location_ids: [],
      is_admin: false,
    },
  });

  const createMutation = useCreateUser();

  useEffect(() => {
    if (isOpen) {
      reset({
        email: '',
        name: '',
        location_ids: [],
        is_admin: false,
      });
    }
  }, [isOpen, reset]);

  const onSubmit = async (data: UserInviteFormData) => {
    try {
      await createMutation.mutateAsync(data);
      showToast('User invited successfully', 'success');
      onClose();
      reset();
    } catch (error) {
      console.error('Error inviting user:', error);
      showToast(
        error instanceof Error ? error.message : 'Failed to invite user',
        'error'
      );
    }
  };

  const handleClose = () => {
    onClose();
    reset();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Invite New User"
      size="md"
    >
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <Input
          label="Email Address"
          type="email"
          required
          placeholder="user@example.com"
          error={errors.email?.message}
          {...register('email')}
          helperText="An invitation email will be sent to this address"
        />

        <Input
          label="Full Name"
          required
          placeholder="John Doe"
          error={errors.name?.message}
          {...register('name')}
        />

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Locations <span className="text-red-500">*</span>
          </label>
          <div className="space-y-2 max-h-48 overflow-y-auto border rounded-lg p-3">
            {locations?.map((location) => (
              <label
                key={location.id}
                className="flex items-center gap-2 cursor-pointer"
              >
                <input
                  type="checkbox"
                  value={location.id}
                  {...register('location_ids')}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm text-gray-900">{location.name}</span>
              </label>
            ))}
          </div>
          {errors.location_ids && (
            <p className="mt-1 text-sm text-red-600">
              {errors.location_ids.message}
            </p>
          )}
        </div>

        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            id="is_admin"
            {...register('is_admin')}
            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
          />
          <label htmlFor="is_admin" className="text-sm font-medium text-gray-700">
            Admin User
          </label>
        </div>

        <div className="flex justify-end gap-3 pt-4 border-t">
          <Button type="button" variant="secondary" onClick={handleClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting ? 'Sending Invitation...' : 'Send Invitation'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
